// Dynamic combo route: /services/$service/$location
// Generates ~762 unique pages (6 services × 127 locations) with LocalBusiness +
// Service + FAQ + Breadcrumb JSON-LD per page.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import type { ServiceId, ServiceMeta } from "@/data/services";
import { ComboServicePageTh } from "@/components/ComboServicePageTh";
import { getLocation, locations } from "@/data/locations";
import type { buildCombo as BuildComboFn } from "@/data/combo-content";
import { getDistrictContext } from "@/data/district-context";
import { dynamicOgImage, ctaDesc, ctaTitle } from "@/lib/seo";
import {
  AeoAnswerBox,
  AtomicStats,
  Breadcrumbs,
  CitationList,
  ContactCTA,
  EntityPanel,
  FaqAccordion,
  HowToSteps,
  PriceMatrix,
  ReviewBadge,
  ServiceCoverageLinks,
  TrustBar,
} from "@/components/seo/Blocks";
import { SITE_CONTENT_REVIEWED } from "@/lib/site-content-meta";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { MapPin } from "lucide-react";
import { RelatedComparisons, categoryForService } from "@/components/RelatedComparisons";
import {
  RelatedServicesInLocation,
  type ServiceKind,
} from "@/components/RelatedServicesInLocation";
import { getReviewerForService, attorneyPersonSchema } from "@/data/service-reviewers";
import { comboTitleTh } from "@/lib/seo-keywords";

const SITE = "https://nyclanguageinstitute.com";


export const Route = createFileRoute("/services/$service/$location")({
  loader: async ({ params }) => {
    const loc = getLocation(params.location);
    if (!loc) throw notFound();
    const [{ SERVICE_EXTRA }, { buildCombo }, { SERVICE_HEAD, resolveServiceIdLight }] =
      await Promise.all([
        import("@/data/services-extra.gen"),
        import("@/data/combo-content"),
        import("@/data/services-head.gen"),
      ]);
    const id = resolveServiceIdLight(params.service) as ServiceId | undefined;
    if (!id) throw notFound();
    const extra = SERVICE_EXTRA[id];
    const merged = { ...SERVICE_HEAD[id], ...extra } as unknown as ServiceMeta;
    return {
      svcMeta: merged,
      service: id,
      locationSlug: params.location,
      extra,
      combo: buildCombo(merged, loc) as ReturnType<typeof BuildComboFn>,
    };
  },
  head: ({ params, loaderData }) => {
    const loc = getLocation(params.location);
    if (!loc || !loaderData)
      return { meta: [{ title: ctaTitle("ไม่พบพื้นที่", "/services/$service/$location", "th") }] };
    const s = loaderData.svcMeta as ServiceMeta;
    const c: ReturnType<typeof BuildComboFn> =
      loaderData?.combo ??
      ({
        metaTitle: `${s.nameTh} ${loc.nameTh}`,
        metaDesc: s.metaDesc,
        keywords: s.keywords ?? [],
        h1: `${s.nameTh} ${loc.nameTh}`,
        faqs: [] as Array<{ q: string; a: string }>,
      } as unknown as ReturnType<typeof BuildComboFn>);
    const url = `${SITE}/services/${s.slug}/${loc.slug}`;
    const ctx = getDistrictContext(loc.slug);
    const ratingKey = `services/${s.slug}/${loc.slug}`;
    const rating = aggregateRatingNode(ratingKey);

    const jsonLdLocalBusiness: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "@id": `${url}#localbusiness`,
      name: `NYC Legal & Notary Service — ${loc.nameTh}`,
      alternateName: "NYC Legal",
      description: c.metaDesc,
      url,
      telephone: "+66-83-249-4999",
      priceRange: "฿฿",
      areaServed: {
        "@type": "Place",
        name: loc.nameTh,
        ...(loc.lat && loc.lng
          ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
          : {}),
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: loc.address ?? "61 ซอยลาดพร้าว 95",
        addressLocality: loc.nameTh,
        postalCode: loc.postalCode ?? "10310",
        addressCountry: "TH",
      },
      openingHours: "Mo-Sa 09:00-18:00",
      aggregateRating: rating,
      dateModified: SITE_CONTENT_REVIEWED,
      makesOffer: {
        "@type": "Offer",
        priceCurrency: "THB",
        price: s.priceFrom,
        itemOffered: { "@type": "Service", name: s.nameTh },
      },
    };

    if (loc.lat && loc.lng) {
      jsonLdLocalBusiness.geo = {
        "@type": "GeoCoordinates",
        latitude: loc.lat,
        longitude: loc.lng,
      };
    }

    const seoTitle = comboTitleTh({
      serviceId: s.id,
      serviceNameTh: s.nameTh,
      loc,
      priceFrom: s.priceFrom,
    });
    const finalTitle = seoTitle.length <= 60 ? seoTitle : c.metaTitle;

    return {
      meta: [
        { title: ctaTitle(finalTitle, "/services/$service/$location", "th") },
        { name: "description", content: ctaDesc(c.metaDesc, "/services/$service/$location", "th") },
        { name: "keywords", content: c.keywords.join(", ") },
        {
          property: "og:title",
          content: ctaTitle(finalTitle, "/services/$service/$location", "th"),
        },
        {
          property: "og:description",
          content: ctaDesc(c.metaDesc, "/services/$service/$location", "th"),
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        {
          property: "og:image",
          content: dynamicOgImage({
            title: c.h1,
            subtitle: `${s.nameTh} · ${loc.nameTh} · เริ่ม ฿${s.priceFrom.toLocaleString()}`,
            badge: s.nameEn.toUpperCase(),
          }),
        },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        {
          name: "twitter:image",
          content: dynamicOgImage({
            title: c.h1,
            subtitle: `${s.nameTh} · ${loc.nameTh}`,
            badge: s.nameEn.toUpperCase(),
          }),
        },
        ...(ctx.transit.length > 0
          ? [{ name: "geo.placename", content: `${loc.nameTh} · ${ctx.transit[0]}` }]
          : []),
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "alternate", hrefLang: "en", href: url.replace(SITE, `${SITE}/en`) },
      ],
      scripts: [
        consolidateGraph([
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "@id": `${url}#webpage`,
              url,
              name: c.metaTitle,
              description: c.metaDesc,
              inLanguage: "th-TH",
              isPartOf: { "@id": `${SITE}#website` },
              about: { "@id": `${url}#service` },
              datePublished: "2024-01-01",
              dateModified: SITE_CONTENT_REVIEWED,
              reviewedBy: { "@id": `${SITE}/about/team#org` },
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: [".aeo-speakable", "h1", "h2"],
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify(jsonLdLocalBusiness),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: c.h1,
              description: c.metaDesc,
              provider: { "@id": `${url}#localbusiness` },
              areaServed: { "@type": "Place", name: loc.nameTh },
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "THB",
                lowPrice: s.priceFrom,
                highPrice: s.priceTo,
              },
              aggregateRating: rating,
              reviewedBy: attorneyPersonSchema(getReviewerForService(loaderData.service), SITE),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: c.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: `ขั้นตอน ${s.nameTh} ใน ${loc.nameTh}`,
              step: s.howTo.map((st, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: st.title,
                text: st.text,
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: s.nameTh,
                  item: `${SITE}/services/${s.slug}`,
                },
                { "@type": "ListItem", position: 4, name: loc.nameTh, item: url },
              ],
            }),
          },
          // Round 12 — Review + AggregateRating + ClaimReview all removed
          // (Google policy: self-serving reviews on a business's own site are
          // ineligible for review snippets and can trigger a manual action).
          // The visible testimonial HTML remains for users.
        ]),
      ],
    };
  },
  component: ComboPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้าที่ค้นหา</h1>
      <Link to="/services" className="mt-4 inline-block text-gold underline">
        กลับไปหน้าบริการทั้งหมด
      </Link>
    </main>
  ),
});

function ComboPage() {
  const { service, locationSlug } = Route.useLoaderData() as {
    service: ServiceId;
    locationSlug: string;
  };
  return <ComboServicePageTh service={service} locationSlug={locationSlug} />;
}
